/**
 * Layout do painel de administração da plataforma.
 * Acesso restrito a system_users (autenticados via /auth/system/login).
 */
import { createFileRoute, Outlet, redirect, Link, useRouter } from '@tanstack/react-router';
import { useSystemAuthStore } from '@/store/system-auth.store';
import { ShieldCheck, Building2, LogOut, Settings, Package, Users, ScrollText, Headphones, Wallet, Gauge, Laptop } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLocation } from '@tanstack/react-router';

export const Route = createFileRoute('/system')({
  beforeLoad: () => {
    const token = localStorage.getItem('system-token');
    if (!token) {
      throw redirect({ to: '/system-login' });
    }
  },
  component: SystemLayout,
});

const navItems = [
  { to: '/system/tenants', label: 'Tenants', icon: Building2 },
  { to: '/system/plans', label: 'Planos e Add-ons', icon: Package },
  { to: '/system/ai-credit-packages', label: 'Créditos de IA', icon: Wallet },
  { to: '/system/tenant-usage', label: 'Uso por Tenant', icon: Gauge },
  { to: '/system/devices', label: 'Dispositivos', icon: Laptop },
  { to: '/system/support', label: 'Suporte', icon: Headphones },
  { to: '/system/users', label: 'Usuários', icon: Users },
  { to: '/system/logs', label: 'Logs', icon: ScrollText },
  { to: '/system/settings', label: 'Configurações', icon: Settings },
];

export function SystemLayout() {
  const { user, logout } = useSystemAuthStore();
  const router = useRouter();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    router.navigate({ to: '/system-login' });
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-950 text-gray-100">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-gray-900 border-r border-gray-800 flex flex-col">
        <div className="p-6 border-b border-gray-800 flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-indigo-400" />
          <div>
            <p className="font-semibold text-white text-sm">Painel da Plataforma</p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                location.pathname.startsWith(to)
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800',
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-800">
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-gray-800 gap-2"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Conteúdo */}
      <main className="flex-1 min-h-0 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
